import React, { useState, useEffect } from 'react';
import { FaPlus, FaRegTrashAlt, FaPencilAlt, FaUserLock, FaEye } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Input, message, Popconfirm, Switch } from 'antd';
import { normalizeString } from '~/Utils';
import api from '~/services/api';
import errorHandler from '~/Utils/errorHandler';
import PageTitle from '~/components/PageTitle';
import Button from '~/components/Button';
import { TableHeader, Table, TableActions } from '~/components/Table';
import Box from '~/components/Box';
import Can from '~/components/Can';
import Cannot from '~/components/Cannot';
import DefaultLayout from '../_layouts/full';
import UserForm from './form';
import RoleForm from './roles';
import { Global } from './styles';

export default function Users() {
  const [loading, setLoading] = useState(false);
  const [recordData, setRecordData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showRoleForm, setShowRoleForm] = useState(false);

  const { t } = useTranslation();

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setRecordData(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const handleNew = () => {
    setShowForm(true);
  };

  const handleEdit = (id) => {
    setSelectedRecord(id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await api.delete(`users/${id}`);
      message.success(t('messages:success'));
      fetchRecords();
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const handleRoleForm = (record) => {
    setLoading(true);
    try {
      setSelectedRecord(record);
    } catch (error) {
      errorHandler(error);
    }
    setShowRoleForm(true);
    setLoading(false);
  };

  const handleChangeActive = async (record) => {
    setLoading(true);
    try {
      await api.put(`/users/${record.id}`, record);
      fetchRecords();
      message.success(t('messages:success'));
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!showForm && !showRoleForm) {
      setSelectedRecord(null);
      fetchRecords();
    }
  }, [showForm, showRoleForm]);

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== '') {
      const results = recordData.filter(
        (item) =>
          item.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 ||
          normalizeString(item.name).toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 ||
          item.email.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
      );
      setTableData(results);
    } else {
      setTableData(recordData);
    }
  }, [recordData, searchTerm]);

  const tableColumns = [
    {
      title: t('screens:users.data.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('screens:users.data.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('screens:users.data.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: '140px',
      align: 'center',
      render: (text, record) => (
        <TableActions>
          <Can permission="@user/edit">
            <Button size="small" title="Editar" onClick={() => handleEdit(record.id)}>
              <FaPencilAlt />
            </Button>
          </Can>
          <Cannot permission="@user/edit">
            <Can permission="@user/view">
              <Button style={{ margin: 'auto' }} size="small" title="Visualizar" onClick={() => handleEdit(record.id)}>
                <FaEye />
              </Button>
            </Can>
          </Cannot>
          <Can permission="@user/delete">
            <Popconfirm
              title={t('messages:confirmDelete')}
              okText={t('messages:yes')}
              onConfirm={() => handleDelete(record.id)}
              cancelText={t('messages:no')}
            >
              <Button size="small" title="Excluir">
                <FaRegTrashAlt />
              </Button>
            </Popconfirm>
          </Can>
          <Can permission="@user/edit">
            <Button onClick={() => handleRoleForm(record)} size="small" title="" style={{ marginLeft: 5 }}>
              <FaUserLock />
            </Button>
          </Can>
          <Can permission="@user/edit">
            <Switch
              name="active"
              size="small"
              style={{ marginTop: '5px' }}
              checked={record.active}
              onChange={(e) => {
                record.active = e;
                delete record.files;
                handleChangeActive(record);
              }}
            />
          </Can>
        </TableActions>
      ),
    },
  ];

  return (
    <DefaultLayout>
      <PageTitle title={t('screens:users.title')}>
        <Can permission="@user/edit">
          <Button loading={loading} color="primary" onClick={handleNew}>
            <FaPlus />
            {t('screens:users.btnNew')}
          </Button>
        </Can>
      </PageTitle>
      <Box>
        <TableHeader>
          <Input.Search onSearch={(value) => setSearchTerm(value)} className="search-field" />
        </TableHeader>
        <Table rowKey="id" loading={loading} dataSource={tableData} columns={tableColumns} />

        <Global />

        <UserForm visible={showForm} userID={selectedRecord} onClose={() => setShowForm(false)} />
        <RoleForm visible={showRoleForm} onClose={() => setShowRoleForm(false)} userData={selectedRecord} />
      </Box>
    </DefaultLayout>
  );
}
